import {useLayoutEffect, useRef, useState} from 'react';
import {useAppDraftGuard} from '../app/navigation/index.js';
import {createConfirmedDraftAutosave} from './confirmedDraftAutosave.js';
import type {DraftSaveState} from './confirmedDraftAutosave.js';

export function useConfirmedDraftAutosave<Draft>(options: {
  ownerKey: string;
  validate: (draft: Draft) => boolean;
  persist: (draft: Draft) => Promise<boolean>;
  isBusy?: () => boolean;
  debounceMs?: number;
}) {
  const latest = useRef(options);
  latest.current = options;
  const [state, setState] = useState<DraftSaveState>('idle');
  const [controller] = useState(() =>
    createConfirmedDraftAutosave<Draft>({
      validate: (draft) => latest.current.validate(draft),
      persist: (draft) => latest.current.persist(draft),
      onState: setState,
      debounceMs: options.debounceMs,
    }),
  );
  useAppDraftGuard({
    ownerKey: options.ownerKey,
    isUnsafeToLeave: () => controller.isDirty() || Boolean(latest.current.isBusy?.()),
    settleBeforeLeave: () => (latest.current.isBusy?.() ? Promise.resolve(false) : controller.flush()),
  });
  useLayoutEffect(() => {
    controller.resume();
    return () => controller.suspend();
  }, [controller]);
  return {controller, state};
}
